import { useCallback } from 'react';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@morressier/alembic';
import _ from 'lodash';
import { useWorkflow } from './useWorkflow';
import { getSubmissionRequestContextFromRoute } from '../contexts/SubmissionDataProvider';
import { ISubmissionFieldResponseObject } from '@publishing-engine/submission-service/src/usecases/submission-fields/types';
import { FormFieldDefinition } from '@publishing-engine/domain/types/article';
import { ICommandHandlerResponse } from '@publishing-engine/submission-service/src/endpoints/api.response-types';

import {
  useAcceptTransfer,
  useEditSubmissionMetadata,
  useSubmitLicensing,
  useAuthorCompleteChangeRequest,
  useSubmitRevision,
  useAuthorEditSubmission
} from 'src/api/commands';
import { IFormFieldState } from 'src/components/FormFieldSet';
import { IFormActionSubtype } from '@publishing-engine/domain/actions/v1/types';
import {
  IEditSubmission,
  ISubmissionFileSet
} from '@publishing-engine/domain/models';
import { ISubmissionDomainActionReference } from '@publishing-engine/domain/actions/v1/commands';
import { CommandName } from './useSubmissionFields';
import { getSourceFileSetSignature } from 'src/pages/Organization/public/Journal/SubmissionUpload/fileSetSignature';

type FieldSetCommandName = Exclude<CommandName, 'ChangeArticleType'>;

type CommandVariablesMap = {
  SubmitRevision: unknown;
  EditSubmission: IEditSubmission;
  SubmitLicensing: unknown;
  AcceptTransferOffer: {
    submissionContextId: string;
    articleWorkflowId: string;
    subtype: IFormActionSubtype;
    formPayload?: unknown;
    oldSubmissionContextId?: string;
    articleTypeCategoryId: string;
  };
  AuthorCompleteChangeRequest: unknown;
  AuthorEditSubmission: unknown;
};

type CommandMutationResultMap = {
  [K in FieldSetCommandName]: UseMutationResult<
    | (ICommandHandlerResponse & { isSuccess: true })
    | { message: any; isSuccess: false },
    unknown,
    CommandVariablesMap[K],
    unknown
  >;
};

type CommandResponse =
  | (ICommandHandlerResponse & { isSuccess: true })
  | { message: any; isSuccess: false };

export const commandWithSubTypes: Record<
  CommandName,
  ISubmissionDomainActionReference
> = {
  SubmitRevision: 'SubmitRevision#fields',
  EditSubmission: 'EditSubmission',
  SubmitLicensing: 'SubmitLicensing#fields',
  AcceptTransferOffer: 'AcceptTransferOffer#fields',
  AuthorCompleteChangeRequest: 'AuthorCompleteChangeRequest#fields',
  AuthorEditSubmission: 'AuthorEditSubmission#fields',
  ChangeArticleType: 'ChangeArticleType'
};

export const useFormFieldSet = (section: string) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { submissionId, submissionContextId, articleWorkflow, organizationId } =
    useWorkflow('submission');
  const { roleContext } = getSubmissionRequestContextFromRoute();

  // Initialize all command mutations
  const submitRevisionMutation = useSubmitRevision({
    submissionId,
    submissionContextId,
    organizationId,
    articleWorkflowId: articleWorkflow.id,
    subtype: 'fields',
    invalidateSubmissionQueries: undefined
  });

  const editSubmissionMutation = useEditSubmissionMetadata({
    articleWorkflowId: articleWorkflow.id,
    submissionContextId,
    submissionId,
    organizationId
  });

  const submitLicensingMutation = useSubmitLicensing({
    submissionId,
    organizationId,
    submissionContextId,
    articleWorkflowId: articleWorkflow.id,
    subtype: 'fields',
    invalidateSubmissionQueries: undefined
  });

  const acceptTransferMutation = useAcceptTransfer(
    submissionId,
    organizationId
  );

  const authorCompleteChangeRequestMutation = useAuthorCompleteChangeRequest({
    submissionId,
    organizationId,
    submissionContextId,
    articleWorkflowId: articleWorkflow.id,
    subtype: 'fields',
    invalidateSubmissionQueries: undefined
  });

  const authorEditSubmissionMutation = useAuthorEditSubmission({
    submissionId,
    organizationId,
    submissionContextId,
    articleWorkflowId: articleWorkflow.id,
    subtype: 'fields',
    invalidateSubmissionQueries: undefined
  });

  // Map command names to their mutation functions
  const commandMutations: CommandMutationResultMap = {
    SubmitRevision: submitRevisionMutation,
    EditSubmission: editSubmissionMutation,
    SubmitLicensing: submitLicensingMutation,
    AcceptTransferOffer: acceptTransferMutation,
    AuthorCompleteChangeRequest: authorCompleteChangeRequestMutation,
    AuthorEditSubmission: authorEditSubmissionMutation
  };

  const getCommandWithSubTypeByCommand = (command: CommandName) => {
    return commandWithSubTypes[command];
  };

  /**
   * Submit multiple fields at once, optimizing by grouping fields that use the same command
   */
  const submitFields = useCallback(
    async (
      updatedValues: Record<string, unknown>,
      fields: IFormFieldState[]
    ) => {
      // Group fields by their command type
      const fieldsByCommand = fields.reduce((acc, field) => {
        // Skip fields that aren't editable
        if (!field.isEditable) {
          return acc;
        }

        // Get the command name from the field
        const commandName = field.editing?.via as CommandName;
        if (!commandName) {
          return acc;
        }

        if (!acc[commandName]) {
          acc[commandName] = [];
        }

        // Extract the value from the nested structure
        const fieldPath = field.formFieldDefinition.caseDataFields.main;
        const fieldValue = _.get(updatedValues, fieldPath);

        // Create a compatible field object
        const fieldObj = {
          field: {
            formFieldDefinition: field.formFieldDefinition,
            value: field.value,
            editing: {
              isEditable: true,
              via: commandName
            }
          } as ISubmissionFieldResponseObject<FormFieldDefinition>,
          value: fieldValue
        };

        acc[commandName].push(fieldObj);

        return acc;
      }, {} as Record<CommandName, Array<{ field: ISubmissionFieldResponseObject<FormFieldDefinition>; value: unknown }>>);

      // Execute each command with its grouped fields
      const results = await Promise.all(
        Object.entries(fieldsByCommand).map(
          async ([commandName, fieldGroups]) => {
            const hasSourceFileChanges = fieldGroups.some(
              ({ field, value }) => {
                const fieldPath = field.formFieldDefinition.caseDataFields.main;
                if (
                  !fieldPath.startsWith('fileSet.') ||
                  !_.has(updatedValues, fieldPath)
                )
                  return false;

                const filePath = fieldPath.slice('fileSet.'.length);
                const previousFileSet: Record<string, unknown> = {};
                const currentFileSet: Record<string, unknown> = {};
                _.set(previousFileSet, filePath, field.previousValue);
                _.set(currentFileSet, filePath, value);

                return (
                  getSourceFileSetSignature(
                    previousFileSet as ISubmissionFileSet
                  ) !==
                  getSourceFileSetSignature(
                    currentFileSet as ISubmissionFileSet
                  )
                );
              }
            );

            // Create a single data object for all fields in this command group
            const data = {
              submissionContextId,
              articleWorkflowId: articleWorkflow.id,
              subtype: 'fields' as const,
              articleTypeCategoryId: articleWorkflow.articleTypeCategoryId,
              ...((commandName === 'SubmitRevision' ||
                commandName === 'AuthorEditSubmission') && {
                isFileUpdated: hasSourceFileChanges
              }),
              ...fieldGroups.reduce((acc, { field, value }) => {
                return _.set(
                  acc,
                  field.formFieldDefinition.caseDataFields.main,
                  value
                );
              }, {})
            };

            // Get the mutation function for this command
            const mutation =
              commandMutations[commandName as FieldSetCommandName];
            if (!mutation) {
              throw new Error(`Unsupported editing command ${commandName}`);
            }

            // Execute the mutation
            return mutation.mutateAsync(data);
          }
        )
      );

      // Check if all commands were successful
      const allSuccessful = results.every(
        (result: CommandResponse) => result?.isSuccess
      );

      if (allSuccessful) {
        // Revalidate the request which should update the submission data
        queryClient.invalidateQueries(['submission-fields', submissionId]);

        toast({
          variant: 'success',
          title: `${section || 'Submission'} Section Edited`
        });

        return true;
      } else {
        toast({
          variant: 'error',
          title: `Error Editing ${section || 'Submission'} Section`,
          description: 'There was an error editing the submission section .'
        });

        return false;
      }
    },
    [queryClient, submissionId, roleContext, toast, commandMutations]
  );

  return {
    submitFields,
    getCommandWithSubTypeByCommand
  };
};
